/**
 * Emit hosts for the `settings` analytics group — 3 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { type WordLibrary } from '@/types';
import { type DateFormat, type SpellingPreference } from '@heidi/schema/ml-scribe';

export function trackUserSettingsDateFormatChanged(dateFormat: DateFormat) {
  trackLegacyEvent({
    name: SegmentObjectName.Settings,
    props: {
      action: SegmentActionName.UserSettingsDateFormatChanged,
      dateFormat,
    },
  });

  // V2 — Amplitude migration
  track({
    event_name: TrackingEventName.SettingsUpdated,
    props: {
      update_action: 'date_format_changed',
      setting_value: dateFormat,
    },
  });
}

export function trackUserSettingsSpellingPreferenceChanged(
  spellingPreference: SpellingPreference
) {
  trackLegacyEvent({
    name: SegmentObjectName.Settings,
    props: {
      action: SegmentActionName.UserSettingsSpellingPreferenceChanged,
      spellingPreference,
    },
  });

  // V2 — Amplitude migration
  track({
    event_name: TrackingEventName.SettingsUpdated,
    props: {
      update_action: 'spelling_preference_changed',
      setting_value: spellingPreference,
    },
  });
}

export function trackUserSettingsWordLibraryAdded(mappings: WordLibrary) {
  trackLegacyEvent({
    name: SegmentObjectName.Settings,
    props: {
      action: SegmentActionName.UserSettingsWordLibraryAdded,
      wordLibrary: mappings,
    },
  });

  // V2 — Amplitude migration
  track({
    event_name: TrackingEventName.SettingsUpdated,
    props: {
      update_action: 'word_library_added',
    },
  });
}
